import React from "react";
import { Input as BaseInput } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import { CustomInputProps, InputSize, InputVariant } from "./Input.types";
import { X } from "lucide-react";
import { ButtonBase } from "@/components/ui/button";
import { NumericFormat } from "react-number-format";

export const Input = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      label,
      type = "text",
      placeholder,
      value,
      onChange,
      onClear,
      inputSize = "md",
      neumorph = false,
      className = "",
      variant = "default",
      disabled = false,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const sizeStyles: Record<InputSize, string> = {
      sm: "px-2 py-1 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    const variantStyles: Record<InputVariant, string> = {
      default: "bg-background border-input",
      outline: "border border-gray-300 focus:ring-primary-500",
      filled: "bg-gray-100 dark:bg-gray-700",
      ghost: "bg-transparent",
    };

    const neumorphStyles =
      "border border-[--color-neutral] dark:border-[--color-neutral-dark] dark:bg-[--secondary-900] dark:text-white";

    const inputClassName = cn(
      sizeStyles[inputSize],
      variantStyles[variant],
      neumorphStyles,
      className
    );

    return (
      <div className="w-full">
        {label && <label className="block text-sm font-medium mb-1">{label}</label>}
        <div className="flex items-center gap-1">
          {type === "number" || type === "percent" ? (
            <NumericFormat
              value={typeof value === "string" || typeof value === "number" ? value : ""}
              allowNegative={false}
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
              suffix={type === "percent" ? "%" : undefined}
              placeholder={placeholder}
              disabled={disabled}
              className={inputClassName}
              customInput={BaseInput}
              getInputRef={ref}
              onFocus={(e) => {
                const input = e.target;
                const position = input.value.indexOf(".");
                if (position > -1) {
                  setTimeout(() => {
                    input.setSelectionRange(position, position);
                  }, 0);
                }
              }}
              onValueChange={(values) => {
                onChange?.({
                  target: {
                    name: props.name,
                    value: values.floatValue ?? "",
                  },
                } as any);
              }}
              {...props}
            />
          ) : (
            <BaseInput
              ref={ref}
              type={type}
              placeholder={placeholder}
              value={value ?? ""}
              onChange={onChange}
              disabled={disabled}
              className={inputClassName}
              {...props}
            />
          )}
          {!!value && onClear && (
            <ButtonBase type="button" onClick={onClear} className="ml-2">
              <X className="h-5 w-5 text-gray-500" />
            </ButtonBase>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = "Input";
